/**
 * Models — typed client for `/api/v1/models`.
 */

import { api } from "./api";

export interface ModelSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  workspace_id: string;
  created_from_dataset_id: string | null;
  promoted_dataset_ids?: string[];
  status: string;
  schema_version: number;
  data_version: number;
  has_canonical_schema: boolean;
  contribution_count: number;
  promoted_contribution_count: number;
  created_at: string;
  updated_at: string;
}

const MODEL_LIST_CACHE_TTL_MS = 10_000;
const modelListCache = new Map<
  string,
  { expiresAt: number; value?: ModelSummary[]; inFlight?: Promise<ModelSummary[]> }
>();

export function invalidateModelList(workspaceId?: string): void {
  if (workspaceId) {
    modelListCache.delete(workspaceId);
    return;
  }
  modelListCache.clear();
}

export function listModels(
  workspaceId: string,
  options?: { force?: boolean; includeSources?: boolean },
): Promise<ModelSummary[]> {
  if (options?.includeSources) {
    return api<ModelSummary[]>(`/api/v1/models?workspace_id=${encodeURIComponent(workspaceId)}&include_source_ids=true`);
  }
  const cached = modelListCache.get(workspaceId);
  const now = Date.now();
  if (!options?.force) {
    if (cached?.value && cached.expiresAt > now) {
      return Promise.resolve(cached.value);
    }
    if (cached?.inFlight) {
      return cached.inFlight;
    }
  }

  const request = api<ModelSummary[]>(
    `/api/v1/models?workspace_id=${encodeURIComponent(workspaceId)}`,
  )
    .then((value) => {
      modelListCache.set(workspaceId, {
        value,
        expiresAt: Date.now() + MODEL_LIST_CACHE_TTL_MS,
      });
      return value;
    })
    .catch((error) => {
      modelListCache.delete(workspaceId);
      throw error;
    });

  modelListCache.set(workspaceId, {
    value: cached?.value,
    expiresAt: cached?.expiresAt ?? 0,
    inFlight: request,
  });
  return request;
}

export function getModel(id: string): Promise<ModelSummary> {
  return api<ModelSummary>(`/api/v1/models/${id}`);
}

export interface CrosstabAxisOption {
  value: string;
  label: string;
  hinted: boolean;
}

export interface CrosstabTableOption {
  table_slug: string;
  table_name: string;
  scope_kind: string;
  member_table_slugs: string[];
  source_id: string | null;
  source_dataset_id: string | null;
  classification: string;
  preferred_table_layout: string | null;
  row_axes: CrosstabAxisOption[];
  time_axes: CrosstabAxisOption[];
  value_axes: CrosstabAxisOption[];
  default_row_axis: string | null;
  default_time_axis: string | null;
  default_value_axis: string | null;
}

export interface CrosstabTablePayload {
  title: string | null;
  row_label: string;
  time_label: string;
  value_label: string;
  detail_columns: Array<{
    key: string;
    label: string;
  }>;
  records: Array<{
    row_value: unknown;
    time_label: string;
    time_sort: number[] | null;
    value: number;
    source_table: string | null;
    source_table_name: string | null;
    source_dataset_id: string | null;
    detail: Record<string, unknown>;
  }>;
}

export interface CrosstabHierarchyState {
  hierarchy_id: string;
  hierarchy_name: string;
  column_ref: string;
  match_target: string;
  drill_member_id: string | null;
  breadcrumbs: Array<{
    member_id: string;
    label: string;
  }>;
  at_root: boolean;
  snapshot: {
    members?: Array<Record<string, unknown>>;
    edges?: Array<Record<string, unknown>>;
  };
}

export interface CrosstabPreview {
  available_tables: CrosstabTableOption[];
  selected_table: string | null;
  selected_row_axis: string | null;
  selected_time_axis: string | null;
  selected_value_axis: string | null;
  table: CrosstabTablePayload | null;
  row_hierarchy: CrosstabHierarchyState | null;
  empty_reason: string | null;
}

export function getModelCrosstabPreview(
  modelId: string,
  params?: {
    table?: string;
    row_axis?: string;
    time_axis?: string;
    value_axis?: string;
    drill_member_id?: string;
    row_dims?: string[];
    column_dims?: string[];
    filter_dims?: string[];
  },
): Promise<CrosstabPreview> {
  const query = new URLSearchParams();
  if (params?.table) query.set("table", params.table);
  if (params?.row_axis) query.set("row_axis", params.row_axis);
  if (params?.time_axis) query.set("time_axis", params.time_axis);
  if (params?.value_axis) query.set("value_axis", params.value_axis);
  if (params?.drill_member_id) {
    query.set("drill_member_id", params.drill_member_id);
  }
  if (params?.row_dims?.length) {
    query.set("row_dims", JSON.stringify(params.row_dims));
  }
  if (params?.column_dims?.length) {
    query.set("column_dims", JSON.stringify(params.column_dims));
  }
  if (params?.filter_dims?.length) {
    query.set("filter_dims", JSON.stringify(params.filter_dims));
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return api<CrosstabPreview>(`/api/v1/models/${modelId}/crosstab-preview${suffix}`);
}

export interface CubeViewDimension {
  id: string;
  label: string;
  kind: string;
}

export interface CubeViewLayout {
  rows: string[];
  columns: string[];
  filters: string[];
}

export interface CubeViewHierarchyNode {
  id: string;
  label: string;
  member_key: string;
  depth: number;
  has_children: boolean;
}

export interface CubeViewFilterOption {
  id: string;
  label: string;
  options: string[];
  /** Version/scenario selector: exactly one member is always required. */
  single_select?: boolean;
  /** Present for bound-hierarchy columns — a level picker (value = member id). */
  hierarchy?: CubeViewHierarchyNode[];
  /** The bound hierarchy's id (when `hierarchy` present) — opens the subset editor. */
  hierarchy_id?: string | null;
}

export interface CubeViewMatrixRow {
  label: string;
  /** Full outer→inner path used by cell actions for a nested display row. */
  context_label?: string | null;
  values: Array<number | null>;
  member_id: string | null;
  /** Expansion key for a nested dimension group (not a governed member id). */
  drill_id?: string | null;
  can_drill: boolean;
  depth: number;
  expanded: boolean;
  /** Excel-style display format for this row's numbers; "" → default. */
  format?: string;
  /** True when this row is a calculated member (value from an MQL formula). */
  calculated?: boolean;
  /** True for a subset "blank after" spacer row (no member, no values). */
  blank?: boolean;
  /** Palette hex for the row label tint (subset apply_color). "" → none. */
  color?: string;
}

export interface CubeViewMatrix {
  columns: string[];
  /** One outer-to-inner dimension path per data column (excludes columns[0]). */
  column_paths?: string[][];
  column_formats?: (string | null)[];
  rows: CubeViewMatrixRow[];
  /** Flow 29 version switchover: the last column index sourced from Actuals,
   *  and the switchover period label — present only when a forecast version is
   *  blended actual-then-plan. Drives the chart's actual→plan seam. */
  switchover_seam_index?: number;
  switchover_period?: string | null;
}

export interface CubeViewPagination {
  page: number;
  page_size: number;
  row_offset: number;
  total_rows: number;
  total_pages: number;
  returned_rows: number;
  has_more: boolean;
}

export interface AggregationProfileContract {
  table_slug: string;
  measure_slug: string;
  semantic_type?: string | null;
  unit?: string | null;
  default_rule: string;
  axis_rules: Record<string, { operator: string; [key: string]: unknown }>;
  components?: Record<string, unknown> | null;
  weight_column?: string | null;
  distinct_column?: string | null;
  snapshot_grain?: string[];
  unit_column?: string | null;
  unit_policy?: string | null;
  unit_conversion_policy?: Record<string, unknown> | null;
  currency_column?: string | null;
  fx_rate_policy?: string | null;
  elimination_policy?: string | null;
  runtime_blocked?: boolean;
  runtime_issues?: Array<{ code: string; message: string }>;
  observed_units?: string[];
  resolved: boolean;
  source: string;
  version?: number | null;
  confidence: number;
  explanation: string;
}

export interface GovernedAggregationProfile {
  id: string | null;
  workspace_id: string | null;
  table_slug: string;
  measure_slug: string;
  semantic_type: string | null;
  unit: string | null;
  profile: AggregationProfileContract;
  evidence: Record<string, unknown>;
  confidence: number;
  source: string;
  version: number | null;
  status: string;
}

export interface CubeViewPreview {
  available_tables: CrosstabTableOption[];
  selected_table: string | null;
  selected_row_axis: string | null;
  selected_time_axis: string | null;
  selected_value_axis: string | null;
  title: string | null;
  value_label: string | null;
  measure_mode: string;
  period_calc: string;
  dimensions: CubeViewDimension[];
  layout: CubeViewLayout;
  rebased_row_detail: string | null;
  effective_filter_values: Record<string, string>;
  effective_filter_value_sets: Record<string, string[]>;
  filter_options: CubeViewFilterOption[];
  /** dimension_id → hierarchy_id for bound dims (rows/cols/filters). */
  bound_hierarchies: Record<string, string>;
  aggregation: AggregationProfileContract;
  pagination: CubeViewPagination;
  matrix: CubeViewMatrix;
  stats: {
    governed_row_count: number;
    rendered_row_count: number;
    rendered_column_count: number;
    total_row_count?: number;
  };
  empty_cause: "filters" | "access_scope" | "no_data" | null;
  empty_reason: string | null;
}

export function confirmAggregationProfile(
  profile: AggregationProfileContract,
): Promise<GovernedAggregationProfile> {
  return api<GovernedAggregationProfile>(
    `/api/v1/aggregation-profiles/${encodeURIComponent(profile.table_slug)}/${encodeURIComponent(profile.measure_slug)}`,
    {
      method: "PUT",
      body: {
        profile,
        semantic_type: profile.semantic_type ?? null,
        unit: profile.unit ?? null,
        evidence: {
          inferred_source: profile.source,
          explanation: profile.explanation,
        },
        confidence: profile.confidence,
      },
    },
  );
}

export function getModelCubeView(
  modelId: string,
  params?: {
    table?: string;
    row_axis?: string;
    time_axis?: string;
    value_axis?: string;
    row_dims?: string[];
    column_dims?: string[];
    filter_dims?: string[];
    filter_values?: Record<string, string>;
    filter_value_sets?: Record<string, string[]>;
    expanded_member_ids?: string[];
    measure_mode?: string;
    period_calc?: string;
    page?: number;
    page_size?: number;
    /** dimension_id → subset_id; scopes+orders that dimension by the subset. */
    subsets?: Record<string, string>;
  },
  options?: {
    signal?: AbortSignal;
    matrixOnly?: boolean;
  },
): Promise<CubeViewPreview> {
  const query = new URLSearchParams();
  if (params?.table) query.set("table", params.table);
  if (params?.row_axis) query.set("row_axis", params.row_axis);
  if (params?.time_axis) query.set("time_axis", params.time_axis);
  if (params?.value_axis) query.set("value_axis", params.value_axis);
  if (params?.row_dims?.length) query.set("row_dims", JSON.stringify(params.row_dims));
  if (params?.column_dims?.length) {
    query.set("column_dims", JSON.stringify(params.column_dims));
  }
  if (params?.filter_dims?.length) {
    query.set("filter_dims", JSON.stringify(params.filter_dims));
  }
  const activeFilters = Object.fromEntries(
    Object.entries(params?.filter_values ?? {}).filter(([, value]) => value.trim().length > 0),
  );
  if (Object.keys(activeFilters).length > 0) {
    query.set("filter_values", JSON.stringify(activeFilters));
  }
  const activeFilterSets = Object.fromEntries(
    Object.entries(params?.filter_value_sets ?? {}).filter(([, values]) =>
      values.some((value) => value.trim().length > 0),
    ),
  );
  if (Object.keys(activeFilterSets).length > 0) {
    query.set("filter_value_sets", JSON.stringify(activeFilterSets));
  }
  if (params?.expanded_member_ids?.length) {
    query.set("expanded_member_ids", JSON.stringify(params.expanded_member_ids));
  }
  if (params?.measure_mode && params.measure_mode !== "value") {
    query.set("measure_mode", params.measure_mode);
  }
  if (params?.period_calc && params.period_calc !== "none") {
    query.set("period_calc", params.period_calc);
  }
  if (params?.page && params.page > 1) {
    query.set("page", String(params.page));
  }
  if (params?.page_size && params.page_size !== 500) {
    query.set("page_size", String(params.page_size));
  }
  if (params?.subsets && Object.keys(params.subsets).length > 0) {
    query.set("subsets", JSON.stringify(params.subsets));
  }
  if (options?.matrixOnly) {
    query.set("matrix_only", "true");
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return api<CubeViewPreview>(`/api/v1/models/${modelId}/cube-view${suffix}`, {
    signal: options?.signal,
  });
}

// --- Cell actions: Top/Bottom-N + Today-view pinned cards -------------------

/** The re-runnable spec of a Top/Bottom-N card (also the pinned-card spec). */
export interface CellTopNSpec {
  table?: string;
  row_axis?: string;
  time_axis?: string;
  value_axis?: string;
  measure_mode?: string;
  filter_values?: Record<string, string>;
  filter_value_sets?: Record<string, string[]>;
  row_dims?: string[];
  row_member_id?: string | null;
  row_value?: string | null;
  period_label?: string | null;
  rank_dimension: string;
  direction?: "top" | "bottom";
  limit?: number;
}

export interface CellTopNResult {
  rank_dimension: string;
  rank_dimension_label: string;
  direction: string;
  limit: number;
  rows: Array<{ label: string; value: number | null }>;
  total: number;
}

export interface CellDetailsSpec {
  table?: string;
  row_axis?: string;
  time_axis?: string;
  value_axis?: string;
  measure_mode?: string;
  filter_values?: Record<string, string>;
  filter_value_sets?: Record<string, string[]>;
  row_dims?: string[];
  column_dims?: string[];
  row_member_id?: string | null;
  row_value?: string | null;
  period_label?: string | null;
  row_label?: string | null;
  column_label?: string | null;
  limit?: number;
}

export interface CellScenarioTarget {
  dataset_id: string;
  table: string;
  row_uid: number;
  measure: string;
  current_value: unknown;
}

export interface CellDetailsResult {
  columns: Array<{ key: string; label: string }>;
  rows: Array<{
    source_table: string | null;
    source_table_name: string | null;
    values: Record<string, unknown>;
    scenario_target: CellScenarioTarget | null;
  }>;
  limit: number;
  truncated: boolean;
}

export interface CellDetailsDeepLink {
  factKey: string | null;
  spec: CellDetailsSpec;
  context: {
    row: string;
    column: string;
    versions: string[];
  };
}

export function parseCellDetailsDeepLink(params: URLSearchParams): CellDetailsDeepLink | null {
  if (params.get("drill") !== "cell-details") return null;
  const table = params.get("table")?.trim();
  const rowAxis = params.get("row_axis")?.trim();
  const timeAxis = params.get("time_axis")?.trim();
  const valueAxis = params.get("value_axis")?.trim();
  const rowValue = params.get("row_value")?.trim();
  const period = params.get("period")?.trim();
  if (!table || !rowAxis || !timeAxis || !valueAxis || !rowValue || !period) {
    return null;
  }

  const versionColumn = params.get("version_column")?.trim();
  const versions = params
    .getAll("version")
    .map((value) => value.trim())
    .filter(Boolean);
  return {
    factKey: params.get("fact")?.trim() || null,
    spec: {
      table,
      row_axis: rowAxis,
      time_axis: timeAxis,
      value_axis: valueAxis,
      measure_mode: "value",
      row_value: rowValue,
      period_label: period,
      row_label: rowValue,
      column_label: period,
      filter_value_sets:
        versionColumn && versions.length > 0 ? { [versionColumn]: versions } : undefined,
      limit: 300,
    },
    context: {
      row: rowValue,
      column: period,
      versions,
    },
  };
}

/**
 * Re-runnable spec for a Value / Line / Donut card pinned from the cube.
 * Unlike TopN (which has its own server endpoint), these are derived from the
 * cube-view matrix, so the spec carries the view selection to re-fetch it plus
 * the row/column selection to re-derive the card. `kind` on the pinned card is
 * "cubecard"; `card` distinguishes the visual.
 */
export interface CubeCardSpec {
  card: "value" | "line" | "donut" | "bar";
  /** The cube-view selection to re-run via getModelCubeView. */
  view: {
    table?: string;
    row_axis?: string;
    time_axis?: string;
    value_axis?: string;
    row_dims?: string[];
    column_dims?: string[];
    filter_dims?: string[];
    filter_values?: Record<string, string>;
    filter_value_sets?: Record<string, string[]>;
    expanded_member_ids?: string[];
    measure_mode?: string;
    period_calc?: string;
  };
  /** Selected rows: match matrix rows by member_id, falling back to label. */
  row_member_ids: Array<string | null>;
  row_labels: string[];
  /** Selected column headers — matched against matrix.columns by label. */
  column_labels: string[];
  /** Optional dotted overlays for line charts. */
  line_overlays?: {
    period_calcs?: Array<"ytd" | "r12" | string>;
    variance?: boolean;
  };
  /**
   * Optional pre-rendered snapshot of exactly the rows/columns this card should
   * show. AI Insights cards set this because their slice (scoped account sets,
   * re-based entities) can't be faithfully reproduced by a live re-fetch of
   * `view`. When present, the card renders from this verbatim; when absent, it
   * re-runs `view` live (real Today pins, which should reflect latest data).
   */
  snapshot?: {
    columns: string[];
    rows: Array<{ label: string; values: Array<number | null> }>;
  };
}

export type PinnedCardSpec = CellTopNSpec | CubeCardSpec;

export interface PinnedCard {
  id: string;
  title: string;
  kind: string;
  spec: PinnedCardSpec;
  position: number;
}

export function getCellTopN(modelId: string, spec: CellTopNSpec): Promise<CellTopNResult> {
  return api<CellTopNResult>(`/api/v1/models/${modelId}/cell-topn`, {
    method: "POST",
    body: spec,
  });
}

export function getCellDetails(modelId: string, spec: CellDetailsSpec): Promise<CellDetailsResult> {
  return api<CellDetailsResult>(`/api/v1/models/${modelId}/cell-details`, {
    method: "POST",
    body: spec,
  });
}

export function listPinnedCards(modelId: string): Promise<PinnedCard[]> {
  return api<PinnedCard[]>(`/api/v1/models/${modelId}/pinned-cards`);
}

export function createPinnedCard(
  modelId: string,
  card: { title: string; kind?: string; spec: PinnedCardSpec },
): Promise<PinnedCard> {
  return api<PinnedCard>(`/api/v1/models/${modelId}/pinned-cards`, {
    method: "POST",
    body: card,
  });
}

export function deletePinnedCard(modelId: string, cardId: string): Promise<void> {
  return api<void>(`/api/v1/models/${modelId}/pinned-cards/${cardId}`, {
    method: "DELETE",
  });
}

export interface CubeAiInsightCard {
  id: string;
  title: string;
  rationale: string;
  kind: "cubecard" | "topn" | string;
  spec: PinnedCardSpec;
  section?: "summary" | "key_figures" | "look_into" | string;
  narrative?: string;
}

export interface CubeAiInsightsPreview {
  cards: CubeAiInsightCard[];
  skipped_reason: string | null;
  data_domain?: string;
  domain_confidence?: number;
  story_headline?: string;
  story_intro?: string;
  summary?: string;
  key_figures_summary?: string;
  look_into_summary?: string;
  story_conclusion?: string;
}

export function previewCubeAiDashboard(
  modelId: string,
  tier: "all" | "tier1" | "tier2" = "all",
  opts?: { refresh?: boolean },
): Promise<CubeAiInsightsPreview> {
  const params = new URLSearchParams();
  if (tier !== "all") params.set("tier", tier);
  if (opts?.refresh) params.set("refresh", "true");
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return api<CubeAiInsightsPreview>(
    `/api/v1/models/${modelId}/cube-ai/dashboard/preview${suffix}`,
    {
      method: "POST",
      body: {},
    },
  );
}

export function buildCubeAiDashboard(
  modelId: string,
  cards: CubeAiInsightCard[],
): Promise<{ created: PinnedCard[] }> {
  return api<{ created: PinnedCard[] }>(`/api/v1/models/${modelId}/cube-ai/dashboard/build`, {
    method: "POST",
    body: { cards },
  });
}

/** Rename a model (and/or update its description). Returns the patched row. */
export function updateModel(
  id: string,
  patch: {
    name?: string;
    description?: string | null;
    icon?: string | null;
    color?: string | null;
  },
): Promise<ModelSummary> {
  return api<ModelSummary>(`/api/v1/models/${id}`, {
    method: "PATCH",
    body: patch,
  }).then((model) => {
    invalidateModelList(model.workspace_id);
    return model;
  });
}

/** Hard-delete a model + all its branches, commits, parquets. */
export function deleteModel(id: string): Promise<void> {
  return api<void>(`/api/v1/models/${id}`, { method: "DELETE" }).then((result) => {
    invalidateModelList();
    return result;
  });
}

// ---------------------------------------------------------------------------
// Multi-source — datasets + relationships
// ---------------------------------------------------------------------------
export interface AttachedDataset {
  id: string;
  name: string;
  content_type: string;
  size_bytes: number;
  status: string;
  created_at: string;
}

export interface RelationshipProposal {
  left_table: string;
  left_column: string;
  right_table: string;
  right_column: string;
  kind: "many_to_one" | "one_to_one" | "one_to_many" | string;
  confidence: number;
  reasoning: string;
  status: "proposed" | "confirmed" | "rejected" | string;
}

export interface RelationshipsResponse {
  summary: string;
  proposals: RelationshipProposal[];
}

export interface AttachDatasetResponse {
  meridian_id: string;
  dataset_id: string;
  table_count: number;
  relationship_count: number;
  summary: string;
}

/** List datasets attached to a model (multi-source view). */
export function listModelDatasets(modelId: string): Promise<AttachedDataset[]> {
  return api<AttachedDataset[]>(`/api/v1/models/${modelId}/datasets`);
}

/** Pre-confirmed cross-dataset join payload. Shape mirrors what
 *  /v1/intelligence/infer-cross-dataset-joins returns; pass this to
 *  attachDatasetToModel to skip the per-attach AI propose_relationships
 *  pass (MULTI_FILE_UPLOAD_CLARIFY Phase 3). */
export type InferredJoin = {
  left_dataset_id: string;
  left_column_slug: string;
  right_dataset_id: string;
  right_column_slug: string;
  kind: string;
  confidence: number;
  confidence_bucket: "high" | "medium" | "low";
  join_policy: "left" | "inner";
  rationale: string;
  /** 'confirmed' = ID-shaped + high confidence + no duplicate ambiguity →
   *  AI uses it without asking. 'proposed' = needs user approval in the
   *  joins-graph UI before AI trusts it. Decided server-side by
   *  cross_dataset_inference_heuristics.resolve_join_status. */
  status: "confirmed" | "proposed";
};

export type StackColumnMapping = {
  dataset_id: string;
  source_column_slug: string;
  source_column_name: string;
  target_column_slug: string;
  confidence: number;
  reason: string;
};

export type StackTargetColumn = {
  name: string;
  slug: string;
  display_name?: string | null;
  dtype?: string | null;
  semantic_type?: string | null;
  nullable: boolean;
};

/** Deterministic proposal to expose several physical datasets as one logical
 * table. The operation always preserves rows; exact repeats are findings, not
 * an implicit DISTINCT. */
export type InferredStack = {
  id: string;
  target_table_slug: string;
  display_name: string;
  member_dataset_ids: string[];
  member_names: string[];
  column_mappings: StackColumnMapping[];
  target_columns: StackTargetColumn[];
  schema_overlap: number;
  confidence: number;
  confidence_bucket: "high" | "medium" | "low";
  status: "confirmed" | "proposed";
  rationale: string;
  warnings: string[];
  estimated_rows: number;
  exact_duplicate_rows: number | null;
  duplicate_groups: number | null;
  operation: "union_all_by_name";
  provenance_columns: string[];
};

export type InferCrossDatasetJoinsResponse = {
  inferred_joins: InferredJoin[];
  inferred_stacks: InferredStack[];
  suggested_model_name: string;
  cached: boolean;
};

/** One AI call across N datasets. Returns inferred joins + a suggested
 *  model name; callers feed the joins through to attachDatasetToModel so
 *  each attach skips its own propose_relationships call.
 *
 *  Backend caches on (workspace, dataset_ids, intelligence_versions) for
 *  24h, so reloading the panel within a day is free. */
export function inferCrossDatasetJoins(
  workspaceId: string,
  datasetIds: string[],
): Promise<InferCrossDatasetJoinsResponse> {
  return api<InferCrossDatasetJoinsResponse>(`/api/v1/intelligence/infer-cross-dataset-joins`, {
    method: "POST",
    body: { workspace_id: workspaceId, dataset_ids: datasetIds },
  });
}

/** Server cap on dataset_ids per infer call. Mirrors max_length in
 *  apps/api/app/schemas/intelligence.py:InferCrossDatasetJoinsIn. Exceeding
 *  it makes the endpoint reject the body with HTTP 422 before any handler
 *  code runs — keep this in sync if the backend cap moves. */
export const INFER_JOINS_BATCH_LIMIT = 20;

/** Infer joins across an arbitrary number of datasets while respecting the
 *  server's per-call cap. Large uploads (e.g. a 34-file onboarding build)
 *  would otherwise 422 the single call and take the whole connect step
 *  down with them.
 *
 *  Strategy: slide an overlapping window of up to INFER_JOINS_BATCH_LIMIT
 *  datasets across the list and merge the per-window joins (deduped by the
 *  directional left/right column tuple). The overlap means adjacent windows
 *  share a few datasets, so most fact→dim joins that straddle a window
 *  boundary are still found. Joins between datasets that never co-occur in
 *  any window can be missed — the per-attach propose pass and the join-graph
 *  UI remain the backstop for those.
 *
 *  Non-throwing: a window that fails (validation, AI, network) is skipped so
 *  one bad batch never blocks model creation. Returns the merged set; an
 *  empty result is a valid outcome the caller treats as "no pre-inferred
 *  joins" rather than an error. */
export async function inferCrossDatasetJoinsBatched(
  workspaceId: string,
  datasetIds: string[],
): Promise<InferCrossDatasetJoinsResponse> {
  if (datasetIds.length <= INFER_JOINS_BATCH_LIMIT) {
    return inferCrossDatasetJoins(workspaceId, datasetIds);
  }

  const limit = INFER_JOINS_BATCH_LIMIT;
  const overlap = Math.min(4, limit - 1); // shared datasets between windows
  const step = limit - overlap;

  const seen = new Set<string>();
  const merged: InferredJoin[] = [];
  const stacksByTarget = new Map<string, InferredStack>();
  let suggestedName = "";
  let anyCached = false;

  for (let start = 0; start < datasetIds.length; start += step) {
    const window = datasetIds.slice(start, start + limit);
    if (window.length < 2) break;
    try {
      const inf = await inferCrossDatasetJoins(workspaceId, window);
      for (const j of inf.inferred_joins ?? []) {
        const key = [
          j.left_dataset_id,
          j.left_column_slug,
          j.right_dataset_id,
          j.right_column_slug,
        ].join("|");
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(j);
      }
      for (const stack of inf.inferred_stacks ?? []) {
        const key = stack.target_table_slug;
        const current = stacksByTarget.get(key);
        if (!current) {
          stacksByTarget.set(key, { ...stack });
          continue;
        }
        const memberIds = Array.from(
          new Set([...current.member_dataset_ids, ...stack.member_dataset_ids]),
        );
        const memberNames = Array.from(
          new Set([...current.member_names, ...stack.member_names]),
        );
        const mappingKeys = new Set(
          current.column_mappings.map(
            (m) => `${m.dataset_id}|${m.source_column_slug}|${m.target_column_slug}`,
          ),
        );
        const mappings = [...current.column_mappings];
        for (const mapping of stack.column_mappings) {
          const mappingKey = `${mapping.dataset_id}|${mapping.source_column_slug}|${mapping.target_column_slug}`;
          if (!mappingKeys.has(mappingKey)) mappings.push(mapping);
          mappingKeys.add(mappingKey);
        }
        const targetSlugs = new Set(current.target_columns.map((c) => c.slug));
        const targetColumns = [...current.target_columns];
        for (const column of stack.target_columns) {
          if (!targetSlugs.has(column.slug)) targetColumns.push(column);
          targetSlugs.add(column.slug);
        }
        stacksByTarget.set(key, {
          ...current,
          member_dataset_ids: memberIds,
          member_names: memberNames,
          column_mappings: mappings,
          target_columns: targetColumns,
          schema_overlap: Math.min(current.schema_overlap, stack.schema_overlap),
          confidence: Math.min(current.confidence, stack.confidence),
          status:
            current.status === "confirmed" && stack.status === "confirmed"
              ? "confirmed"
              : "proposed",
          warnings: Array.from(new Set([...current.warnings, ...stack.warnings])),
          estimated_rows: Math.max(current.estimated_rows, stack.estimated_rows),
          exact_duplicate_rows: null,
          duplicate_groups: null,
        });
      }
      if (!suggestedName && inf.suggested_model_name.trim()) {
        suggestedName = inf.suggested_model_name.trim();
      }
      anyCached = anyCached || inf.cached;
    } catch {
      // Skip this window; remaining windows and the model build proceed.
    }
    if (start + limit >= datasetIds.length) break; // tail covered
  }

  return {
    inferred_joins: merged,
    inferred_stacks: Array.from(stacksByTarget.values()),
    suggested_model_name: suggestedName,
    cached: anyCached,
  };
}

/** Attach an additional dataset to a model.
 *
 *  When `inferredJoins` is omitted, the backend runs its own AI
 *  relationship-proposal pass per attach — the legacy / single-dataset
 *  path. When provided (from a prior inferCrossDatasetJoins call), the
 *  backend uses the pre-confirmed joins and skips the AI call, cutting
 *  cost from N AI-calls to 1 across a multi-file build. */
export function attachDatasetToModel(
  modelId: string,
  datasetId: string,
  inferredJoins?: InferredJoin[],
  deferEnrichment = false,
  stackGroups?: InferredStack[],
): Promise<AttachDatasetResponse> {
  return api<AttachDatasetResponse>(`/api/v1/models/${modelId}/datasets`, {
    method: "POST",
    body: {
      dataset_id: datasetId,
      ...(inferredJoins && inferredJoins.length > 0 ? { inferred_joins: inferredJoins } : {}),
      ...(stackGroups && stackGroups.length > 0 ? { stack_groups: stackGroups } : {}),
      defer_enrichment: deferEnrichment,
    },
  });
}

/** Get the AI's proposed joins between attached datasets. */
export function fetchModelRelationships(modelId: string): Promise<RelationshipsResponse> {
  return api<RelationshipsResponse>(`/api/v1/models/${modelId}/relationships`);
}

/** Confirm or reject a single AI-proposed relationship. Identified by
 *  the 4-tuple (left_table, left_column, right_table, right_column)
 *  rather than by index — so the choice survives a re-propose run
 *  that re-orders proposals. */
// ---------------------------------------------------------------------------
// Join Readiness — the honest receipt (Phase 1). Read-only; mirrors
// `services/join_readiness.py: build_join_receipt`. Every line the
// receipt renders cites a verdict the validator actually computed.
// ---------------------------------------------------------------------------
export interface JoinReadinessJoinItem {
  kind: "join";
  left_table: string;
  left_column: string;
  right_table: string;
  right_column: string;
  headline: string;
  evidence: string;
  /** ready_now only — what questions the join enables; null when either
   *  side lacks the typed columns to back the claim (honesty rule). */
  enables?: string | null;
  /** confirm_first only — always "confirm_join". */
  action?: string;
  /** not_possible only — concrete remediation in plain language. */
  path_forward?: string;
}

export interface JoinReadinessIsolatedItem {
  kind: "isolated_table";
  table: string;
  headline: string;
  evidence: string;
  path_forward: string;
}

export interface JoinReadinessGrainAlignItem {
  kind: "grain_align";
  coarse_table: string;
  fine_table: string;
  target_grain: string;
  headline: string;
  evidence: string;
  /** confirm_first only — always "confirm_grain_align". */
  action?: string;
  /** ready_now only — the measures that still can't be compared at the
   *  coarser period, stated as the honest limit; null when none. */
  enables?: string | null;
}

export interface JoinReadinessKeyMappingItem {
  kind: "key_mapping";
  left_table: string;
  left_column: string;
  right_table: string;
  right_column: string;
  matched: number;
  headline: string;
  evidence: string;
  /** confirm_first only — always "confirm_key_mapping". */
  action?: string;
  /** the values that still match nothing, stated as the honest limit. */
  enables?: string | null;
}

/** One conformed dimension of a planning/budget table and how it binds to
 *  the fact: a time dim carries `compare_at` (the grain to roll up to); a
 *  category dim carries the `fact_table`.`fact_column` it conforms on, or
 *  `unbound` when nothing matched. */
export interface JoinReadinessConformedDim {
  column: string;
  kind: "time" | "category";
  compare_at?: string;
  fact_table?: string;
  fact_column?: string;
  unbound?: boolean;
}

/** A budget/target/plan table held at a coarse grain — compared on its
 *  conformed dimensions rather than joined on a single key. Surfaced so the
 *  table reads as a conformed comparison, not an "orphaned" island. */
export interface JoinReadinessConformedItem {
  kind: "conformed_join";
  planning_table: string;
  target_measures: string[];
  conformed_dims: JoinReadinessConformedDim[];
  headline: string;
  evidence: string;
  enables?: string | null;
}

export interface JoinReadinessRowStackItem {
  kind: "row_stack";
  table: string;
  member_dataset_ids: string[];
  headline: string;
  evidence: string;
  enables?: string | null;
}

export type JoinReadinessItem =
  | JoinReadinessJoinItem
  | JoinReadinessIsolatedItem
  | JoinReadinessGrainAlignItem
  | JoinReadinessKeyMappingItem
  | JoinReadinessConformedItem
  | JoinReadinessRowStackItem;

export interface JoinReadinessReceiptOut {
  ready_now: JoinReadinessItem[];
  confirm_first: JoinReadinessItem[];
  not_possible: JoinReadinessItem[];
  counts: {
    ready_now: number;
    confirm_first: number;
    not_possible: number;
  };
}

export function fetchJoinReadiness(modelId: string): Promise<JoinReadinessReceiptOut> {
  return api<JoinReadinessReceiptOut>(`/api/v1/models/${modelId}/join-readiness`);
}

export function patchRelationship(
  modelId: string,
  payload: {
    left_table: string;
    left_column: string;
    right_table: string;
    right_column: string;
    status: "proposed" | "confirmed" | "rejected";
  },
): Promise<RelationshipsResponse> {
  return api<RelationshipsResponse>(`/api/v1/models/${modelId}/relationships`, {
    method: "PATCH",
    body: payload,
  });
}

/** Confirm or reject a grain alignment (Join Readiness Phase 2). Returns
 *  the rebuilt receipt so the caller can refresh in place. */
export function patchGrainAlignment(
  modelId: string,
  payload: {
    coarse_table: string;
    fine_table: string;
    status: "proposed" | "confirmed" | "rejected";
  },
): Promise<JoinReadinessReceiptOut> {
  return api<JoinReadinessReceiptOut>(`/api/v1/models/${modelId}/grain-alignment`, {
    method: "PATCH",
    body: payload,
  });
}

/** Confirm or reject a cross-source key mapping (Join Readiness Phase 3).
 *  Returns the rebuilt receipt. */
export function patchKeyMapping(
  modelId: string,
  payload: {
    left_table: string;
    left_column: string;
    right_table: string;
    right_column: string;
    status: "proposed" | "confirmed" | "rejected";
  },
): Promise<JoinReadinessReceiptOut> {
  return api<JoinReadinessReceiptOut>(`/api/v1/models/${modelId}/key-mapping`, {
    method: "PATCH",
    body: payload,
  });
}

/** Manually re-trigger AI relationship analysis. Preserves user-confirmed
 *  / -rejected statuses on existing proposals. */
export function refreshRelationships(modelId: string): Promise<RelationshipsResponse> {
  return api<RelationshipsResponse>(`/api/v1/models/${modelId}/relationships/refresh`, {
    method: "POST",
    body: {},
  });
}

/** Result of /api/v1/models/{id}/rebuild-schema. Meridians have no commit
 *  graph; the FE typically refetches the surface that triggered the action
 *  (DataSourcesPage refresh, Ask history reload). */
export interface RebuildSchemaResponse {
  meridian_id: string;
  table_count: number;
  relationship_count: number;
}

/** Rebuild the model's live HEAD commit from the CURRENTLY-attached
 *  datasets. Use when the model UI shows datasets but Ask Meridian
 *  reports "no schema tables present" — that means the HEAD commit
 *  is referencing a deleted/stale dataset and the snapshot is empty.
 *  This rewinds and re-mints the snapshot from current state. Safe
 *  to retry; idempotent. */
export function rebuildSchema(modelId: string): Promise<RebuildSchemaResponse> {
  return api<RebuildSchemaResponse>(`/api/v1/models/${modelId}/rebuild-schema`, {
    method: "POST",
    body: {},
  });
}

/** Create a brand-new join proposal manually (user picked the columns
 *  themselves rather than accepting the AI's suggestion). The new join
 *  lands as confirmed by default — the user explicitly opted in. */
export function createRelationship(
  modelId: string,
  payload: {
    left_table: string;
    left_column: string;
    right_table: string;
    right_column: string;
    kind?: "many_to_one" | "one_to_one" | "one_to_many";
    status?: "proposed" | "confirmed";
    /** Omit to inherit the safe LEFT default; set to "inner" only when the
     *  user makes the ambiguous-band choice in the join editor. */
    join_policy?: "left" | "inner";
  },
): Promise<RelationshipsResponse> {
  return api<RelationshipsResponse>(`/api/v1/models/${modelId}/relationships`, {
    method: "POST",
    body: { kind: "many_to_one", status: "confirmed", ...payload },
  });
}

/** Read-only measurement of a candidate join against the real data. Mirrors
 *  JoinAssessmentOut in apps/api/app/api/v1/models.py. `checked` is false when
 *  the data couldn't be measured (parquet missing / column absent). */
export interface JoinAssessment {
  checked: boolean;
  overlap_ratio: number | null;
  fanout_factor: number | null;
  right_unique: boolean | null;
  warnings: string[];
  /** LEFT/INNER recommendation. `should_ask_policy` is true only in the
   *  ambiguous band (clean key, 50-98% overlap) where the choice is a real
   *  judgment call; otherwise the gate just shows the safe default. */
  recommended_policy: "left" | "inner";
  policy_confidence: "high" | "medium" | "low";
  should_ask_policy: boolean;
  policy_note: string;
}

/** Measure whether two columns actually overlap, WITHOUT saving a join.
 *  Powers the join editor's live "X% of values match" readout. */
export function assessRelationship(
  modelId: string,
  payload: {
    left_table: string;
    left_column: string;
    right_table: string;
    right_column: string;
  },
): Promise<JoinAssessment> {
  return api<JoinAssessment>(`/api/v1/models/${modelId}/relationships/assess`, {
    method: "POST",
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Tables + columns surfaced from the model's HEAD schema_snapshot. The
// JoinEditDialog uses these to populate dropdowns so the user can pick
// columns by name rather than typing slugs.
// ---------------------------------------------------------------------------
export interface ModelColumn {
  name: string;
  semantic_type?: string | null;
  description?: string | null;
  /** AI/heuristic-derived friendly label ('Department ID' for raw
   *  name 'dept_id'). When present, prefer this over the raw name in
   *  user-facing UI; the raw name stays available in parentheses for
   *  power-user disambiguation. */
  display_name?: string | null;
  /** Up to ~6 sample values from the column. Drives the join-editor
   *  preview so the user can eyeball that values actually match on
   *  both sides before confirming a join. */
  sample_values?: string[];
  /** Raw analyzer dtype: integer / number / string / datetime / boolean.
   *  Used to flag obvious type mismatches in the join editor. */
  dtype?: string | null;
  /** User-set role override (key / date / measure / dimension) from the
   *  Columns view. When absent, the FE derives a role. Setting it also
   *  canonicalises the backend semantic_type so the override drives
   *  analysis. */
  role?: string | null;
}

export interface ModelTable {
  slug: string;
  name?: string | null;
  columns: ModelColumn[];
  row_count?: number | null;
}

/** Fetch the table+column inventory for a model's live HEAD. Used by the
 *  manual join-editor dropdowns. Returns [] for an empty model. */
export function listModelTables(modelId: string): Promise<ModelTable[]> {
  return api<ModelTable[]>(`/api/v1/models/${modelId}/tables`);
}

/** How a source column maps into a governed meridian dimension. */
export interface GovernedDimension {
  table_slug: string;
  column_slug: string | null;
  column_name: string | null;
  /** company | version | time | null (plain bound-hierarchy dimension). */
  semantic_role: string | null;
  hierarchy_id: string | null;
  hierarchy_name: string | null;
  /** True when Meridian defaulted the dimension (no column mapped). */
  defaulted: boolean;
}

/** The column → governed-dimension mapping for the Data Sources view. */
export function listGovernedDimensions(modelId: string): Promise<GovernedDimension[]> {
  return api<GovernedDimension[]>(`/api/v1/models/${modelId}/governed-dimensions`);
}

export type ColumnRole = "key" | "date" | "measure" | "dimension" | "attribute";

export interface ColumnRoleResponse {
  table: string;
  column: string;
  role: string | null;
  semantic_type: string | null;
}

/** Set or clear a column's role override. `role: null` clears it and
 *  restores the auto-derived classification. The backend canonicalises the
 *  column's semantic_type from the role so analysis respects the choice. */
export function patchColumnRole(
  modelId: string,
  payload: { table: string; column: string; role: ColumnRole | null },
): Promise<ColumnRoleResponse> {
  return api<ColumnRoleResponse>(`/api/v1/models/${modelId}/columns`, {
    method: "PATCH",
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Proactive insights — AI-interpreted patterns surfaced on the model's
// Insights tab. See backend services/insights_proactive.py.
// ---------------------------------------------------------------------------
export type InsightKind =
  | "correlation"
  | "hidden_segment"
  | "anomaly"
  | "lagged_correlation"
  | "regime_shift"
  | "cohort_pattern"
  | "trend"
  | "seasonality"
  | "concentration"
  | "threshold_effect"
  | "what_happens_after"
  | "breakpoint"
  // Data-Quality analysts (DATA_QUALITY_ANALYSTS.md).
  | "alias_split"
  | "missing_not_at_random"
  | "unexpected_sign"
  | "rare_category"
  | "unit_mismatch"
  | "duplicate_record"
  | "field_inconsistency"
  | "join_integrity"
  | "event_order"
  | "exclusion_pattern"
  | "ratio_anomaly"
  | "peer_benchmark_gap"
  | "distribution_shift"
  | "pareto_shift"
  | "mix_shift"
  | "price_variance"
  | "denominator_shift"
  | "reconciliation_gap"
  | "forecast_bias"
  | "segmented_trend"
  | "coupling_divergence"
  | string;

/** "Try this in a branch" CTA emitted by the AI on insights with an
 *  obvious mutational follow-up. The frontend renders it as a button
 *  next to the existing "Ask deeper" chip; on click it navigates to
 *  the live branch's BranchWorkPage with `branch_prompt` pre-seeded
 *  into the composer, kicking off Branch Authoring Loop. */
export interface InsightSuggestedAction {
  label: string;
  branch_prompt: string;
}

export interface ModelInsight {
  kind: InsightKind;
  title: string;
  description: string;
  confidence: number;
  follow_up_question: string;
  evidence: Record<string, unknown>;
  /** Null when the insight has no obvious mutational follow-up — UI
   *  hides the "Try as branch" CTA in that case. */
  suggested_action?: InsightSuggestedAction | null;
  detector_ref?: string | null;
  detector_version?: string | null;
  context_reason?: string | null;
}

export interface DetectorFact {
  id?: string | null;
  kind: "anomaly" | "trend" | "concentration" | string;
  detector_version: string;
  data_version?: string | null;
  identity: Record<string, unknown>;
  evidence: Record<string, unknown>;
  baseline: Record<string, unknown>;
  threshold: Record<string, unknown>;
  severity: number;
  confidence: number;
  polarity: string;
  detected_at?: string | null;
  detector_ref: string;
}

export interface ModelInsightsResponse {
  insights: ModelInsight[];
  detector_facts?: DetectorFact[];
  series_analysed: number;
  skipped_reason?: string | null;
  /** Compact pipeline trace -- "Schema 15 tables -> 1 pickable
   *  (14 filtered: no numeric/date pair). Analysed dim_drg_csv
   *  (23 rows)." Surfaced under the main message so the user can see
   *  WHY analysis was thin even on success. */
  diagnostic_summary?: string | null;
  /** Analyst standfirst — the cross-card "why it matters" exec summary
   *  written by the deep-tier narrator. Null when narration didn't run
   *  or failed open. Rendered as a lede above the cards. */
  summary?: string | null;
  /** Confirmed member-profile version used for presentation ranking only. */
  context_profile_version?: number | null;
}

/** Trigger the proactive-insights pipeline on a model. ~10-30s sync. */
export function analyzeModelInsights(modelId: string): Promise<ModelInsightsResponse> {
  return api<ModelInsightsResponse>(`/api/v1/models/${modelId}/insights/analyze`, {
    method: "POST",
    body: {},
  });
}

export function listModelDetectorFacts(modelId: string): Promise<DetectorFact[]> {
  return api<DetectorFact[]>(`/api/v1/models/${modelId}/insights/facts`);
}

// ---------------------------------------------------------------------------
// Model contract — "what Meridian believes about this data".
// Read-model over schema_snapshot + refresh policies; see
// docs/specs/GUARDRAIL_ARCHITECTURE.md §4 and api/v1/models.py
// `get_model_contract`.
// ---------------------------------------------------------------------------

export type ClaimStatus = "observed" | "inferred" | "assumed" | "confirmed";

export interface ContractClaim {
  id: string;
  kind: string;
  subject: Record<string, unknown>;
  statement: string;
  status: ClaimStatus;
  evidence: string | null;
  confidence: number | null;
  source: string | null;
  consequence: "high" | "medium" | "low";
}

export interface ModelContractOut {
  claims: ContractClaim[];
  counts: Record<string, number>;
}

export function fetchModelContract(modelId: string): Promise<ModelContractOut> {
  return api<ModelContractOut>(`/api/v1/models/${modelId}/contract`);
}

/** Mark / unmark a column as a non-aggregatable version/scenario
 *  dimension (or allow summing with `aggregatable: true`). Backend:
 *  PATCH /models/{id}/scenario-dimension. */
export function patchScenarioDimension(
  modelId: string,
  payload: {
    table: string;
    column?: string | null;
    values?: string[] | null;
    default_value?: string | null;
    aggregatable?: boolean;
    /** Telemetry only — which surface the change came from. */
    surface?: string | null;
  },
): Promise<Record<string, unknown>> {
  return api<Record<string, unknown>>(`/api/v1/models/${modelId}/scenario-dimension`, {
    method: "PATCH",
    body: payload,
  });
}

/** Correct or clear the NAME-inferred unit scale (thousands / millions) of a
 *  numeric column. `disabled: true` says it is not scaled at all; `scale`
 *  re-labels it; omit both to clear the override. Backend:
 *  PATCH /models/{id}/unit-scale. */
export function patchUnitScale(
  modelId: string,
  payload: {
    table: string;
    column: string;
    disabled?: boolean;
    scale?: number | null;
    surface?: string | null;
  },
): Promise<Record<string, unknown>> {
  return api<Record<string, unknown>>(`/api/v1/models/${modelId}/unit-scale`, {
    method: "PATCH",
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Import questions — the interaction broker's question budget (max 3).
// See docs/specs/GUARDRAIL_ARCHITECTURE.md §5.2 and api/v1/models.py
// `get_import_questions`. Answering happens through the existing PATCH
// clients above; dismissing retires the question permanently.
// ---------------------------------------------------------------------------

export interface ImportQuestionOption {
  label: string;
  action: {
    type: "relationship" | "grain_alignment" | "key_mapping" | "scenario_dimension" | string;
    payload: Record<string, unknown>;
  };
}

export interface ImportQuestion {
  claim_id: string;
  claim_kind: string;
  statement: string | null;
  evidence: string | null;
  question: string;
  options: ImportQuestionOption[];
}

export function fetchImportQuestions(modelId: string): Promise<{ questions: ImportQuestion[] }> {
  return api<{ questions: ImportQuestion[] }>(`/api/v1/models/${modelId}/questions`);
}

export function dismissImportQuestion(
  modelId: string,
  payload: { claim_id: string; claim_kind: string },
): Promise<{ dismissed: string }> {
  return api<{ dismissed: string }>(`/api/v1/models/${modelId}/questions/dismiss`, {
    method: "POST",
    body: payload,
  });
}
